import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import type { ISeason } from '../../api/client';
import { useToast } from '../../context/ToastContext';
import { Button } from '../ui';
import CollapsibleWidget from '../CollapsibleWidget';
import SeasonStatusBadge from './SeasonStatusBadge';
import { seasonLabel } from '../../utils/seasonLabel';
import './SeasonsSidebar.css';
import { translateError } from '../../utils/errorMessages';

interface SeasonsSidebarProps {
  courseId: number;
  /** All seasons of the course — the page owns the fetch. */
  seasons: ISeason[];
  /** The page-selected season id (drives Settings + Payments). */
  selectedSeasonId: number | null;
  /** Select a season (view its details). */
  onSelectSeason: (id: number) => void;
  /** Open the SeasonForm modal for a specific (editable) season. */
  onEditSeason: (id: number) => void;
  /** Whether the current user may edit/archive/restore/prepare seasons. */
  canManage: boolean;
  /** Open the renewal wizard (owned by the page). */
  onPrepareNextSeason: () => void;
  /** Called after an archive/restore so the page can refetch everything. */
  onSeasonChanged: () => void;
}

/**
 * Seasons picker rendered as row-widgets (the member Memberships/Payments widget
 * pattern): a "Seasons" CollapsibleWidget for current/upcoming and a collapsed
 * "History" widget for past/archived. Each season is a compact, selectable row;
 * selecting drives the Settings + Payments panels. Season EDITING lives on the
 * Settings SeasonInfoPanel (Edit → SeasonForm modal), not here. Styling is in
 * SeasonsSidebar.css (theme tokens + relative units).
 */
const SeasonsSidebar: React.FC<SeasonsSidebarProps> = ({
  courseId,
  seasons,
  selectedSeasonId,
  onSelectSeason,
  onEditSeason,
  canManage,
  onPrepareNextSeason,
  onSeasonChanged,
}) => {
  const { t, i18n } = useTranslation();
  const { addToast } = useToast();
  const [busyId, setBusyId] = useState<number | null>(null);

  const currentUpcoming = useMemo(
    () => seasons.filter((s) => s.status === 'current' || s.status === 'upcoming'),
    [seasons],
  );
  const history = useMemo(
    () => seasons.filter((s) => s.status === 'past' || s.status === 'archived'),
    [seasons],
  );

  const handleArchiveToggle = async (season: ISeason) => {
    setBusyId(season.id);
    try {
      if (season.archivedAt != null) {
        await api.courses.seasons.unarchive(courseId, season.id);
        addToast(t('courses.seasons.unarchived'), 'success');
      } else {
        await api.courses.seasons.archive(courseId, season.id);
        addToast(t('courses.seasons.archived'), 'success');
      }
      onSeasonChanged();
    } catch (err: any) {
      addToast(translateError(err, t, 'common.error'), 'error');
    } finally {
      setBusyId(null);
    }
  };

  const renderRow = (s: ISeason) => {
    const selected = s.id === selectedSeasonId;
    // Editability follows the season lifecycle (never `isActive`).
    const editable = s.status === 'current' || s.status === 'upcoming';
    return (
      <div
        key={s.id}
        role="button"
        tabIndex={0}
        aria-pressed={selected}
        className={`season-row ${selected ? 'selected' : ''}`}
        onClick={() => onSelectSeason(s.id)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onSelectSeason(s.id);
          }
        }}
      >
        <div className="season-row__head">
          <span className="season-row__label">
            {seasonLabel(s.termStart, s.termEnd, t, i18n.language)}
          </span>
          {s.isActive && <span className="season-row__active">{t('courses.seasons.activeMarker')}</span>}
          <SeasonStatusBadge status={s.status} />
        </div>
        {canManage && (
          <div className="season-row__actions">
            {editable && (
              <Button
                variant="primary"
                size="sm"
                onClick={(e) => {
                  e.stopPropagation();
                  onEditSeason(s.id);
                }}
              >
                {t('courses.seasonInfo.edit')}
              </Button>
            )}
            <Button
              variant="secondary"
              size="sm"
              loading={busyId === s.id}
              onClick={(e) => {
                e.stopPropagation();
                handleArchiveToggle(s);
              }}
            >
              {s.archivedAt != null ? t('courses.seasons.unarchive') : t('courses.seasons.archive')}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <CollapsibleWidget
        title={t('courses.seasonsSidebar.title')}
        defaultExpanded={true}
        storageKey={`course-${courseId}-seasons`}
      >
        {canManage && (
          <div className="seasons-widget__toolbar">
            <Button variant="primary" size="sm" fullWidth onClick={onPrepareNextSeason}>
              {t('courses.renew.button')}
            </Button>
          </div>
        )}
        {currentUpcoming.length === 0 ? (
          <p className="seasons-widget__empty">{t('courses.seasons.noneCurrentUpcoming')}</p>
        ) : (
          <div className="seasons-widget__list">{currentUpcoming.map(renderRow)}</div>
        )}
      </CollapsibleWidget>

      {history.length > 0 && (
        <CollapsibleWidget
          title={t('courses.seasonsSidebar.history')}
          defaultExpanded={false}
          storageKey={`course-${courseId}-season-history`}
        >
          <p className="form-help-text seasons-widget__hint">{t('courses.seasonsSidebar.viewOnlyHint')}</p>
          <div className="seasons-widget__list">{history.map(renderRow)}</div>
        </CollapsibleWidget>
      )}
    </>
  );
};

export default SeasonsSidebar;
